import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface DashboardMetrics {
  activeTrackings: number;
  retryToday: number;
  sqsQueue: number;
  sqsDLQ: number;
  trackingSuccess: number;
  trackingError: number;
  trackingTotal: number;
  gateways: Record<string, number>;
  lastUpdate: string;
  services: {
    apiCore: string;
    shipper: string;
    colibriTracking: string;
    sqsQueue: string;
  };
}

export interface TrackingVolume {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
  }>;
}

export interface RecentTracking {
  timestamp: string;
  active: number;
  processed: number;
  success: number;
  error: number;
  sqs: number;
}

export interface RecentDelivery {
  id: string;
  customer: string;
  status: string;
  time: string;
  carrier: string;
  location: string;
}

export interface GatewayStat {
  gateway: string;
  total: number;
  active: number;
  delivered: number;
  intransit: number;
  posted: number;
  error: number;
  deliveryRate: string;
}

export interface TrackingStatusItem {
  trackingId: string;
  gateway: string;
  trackedAt: string;
  errorMessage?: string;
}

export interface DailyScheduledItem {
  trackingId: string;
  gateway: string;
  scheduledAt: string;
  status: 'scheduled' | 'processing' | 'processed' | 'success' | 'error' | 'timeout';
  objectStatus?: 'active' | 'delivered' | 'cancelled';
  errorMessage?: string;
  trackedAt: string;
}

export interface RecentTrackingStatus {
  items: DailyScheduledItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface TrackingStatusFilters {
  date?: string;
  search?: string;
  status?: string;
  gateway?: string;
  page?: number;
  limit?: number;
}

export const dashboardService = {
  async getMetrics(): Promise<DashboardMetrics> {
    const response = await api.get('/dashboard/metrics');
    return response.data;
  },

  async getTrackingVolume(hours: number = 24): Promise<TrackingVolume> {
    const response = await api.get('/dashboard/tracking-volume', {
      params: { hours }
    });
    return response.data;
  },

  async getRecentTrackings(hours: number = 24): Promise<{ trackings: RecentTracking[] }> {
    const response = await api.get('/dashboard/recent-trackings', {
      params: { hours }
    });
    return response.data;
  },

  async getRecentDeliveries(limit: number = 10): Promise<{ deliveries: RecentDelivery[] }> {
    const response = await api.get('/dashboard/recent-deliveries', {
      params: { limit }
    });
    return response.data;
  },

  async getGatewayStats(): Promise<{ stats: GatewayStat[] }> {
    const response = await api.get('/dashboard/gateway-stats');
    return response.data;
  },

  async getRecentTrackingStatus(filters?: TrackingStatusFilters): Promise<RecentTrackingStatus> {
    const response = await api.get('/tracking/status/recent', {
      params: filters
    });
    return response.data.data;
  },

  async getTrackingFilters(): Promise<{ statuses: string[], gateways: string[] }> {
    const response = await api.get('/tracking/status/filters');
    return response.data.data;
  },

  async getTrackingMetrics(): Promise<{
    total: number;
    delivered: number;
    active: number;
    error: number;
    errorActive: number;
    errorInactive: number;
    success: number;
    successRate: number;
    processedToday: number;
    statusBreakdown: Record<string, number>;
    gatewayBreakdown: Record<string, number>;
  }> {
    const response = await api.get('/tracking/status/metrics');
    return response.data.data;
  },
};

export interface TrackingEvent {
  datetime: string;
  status: string;
  description: string;
  location?: string;
  origin?: any;
  destiny?: any;
}

export interface DynamoDBData {
  track_status: string;
  object_status: string;
  last_event: {
    description: string;
    datetime: string;
    location: string;
    object_status: string;
    status: string;
    origin?: any;
  } | null;
  last_location: string;
  last_status: string;
  account_id: string;
  client_id: string;
  retry_count: number;
  retry_at: string;
  track_delay: number;
  date_created: string;
  date_updated: string;
  shipment_id: string;
  notify: Array<{
    channel: string;
    to: string;
  }>;
  log_id: string;
}

export interface APIData {
  track_status: string;
  object_status: string;
  events: TrackingEvent[];
  detail: {
    description: string;
    location: string | null;
  } | null;
  account_id: string;
  client_id: string;
  retry_count: number;
  retry_at: string;
  track_delay: number;
  timestamp: string;
  shipment_id: string;
}

export interface TrackingLookup {
  gateway: string;
  trackId: string;
  timestamp: string;
  api: APIData | null;
  dynamodb: DynamoDBData | null;
}

export const trackingService = {
  async directLookup(gateway: string, trackId: string): Promise<TrackingLookup> {
    const response = await api.get(`/tracking/lookup/${gateway}/${trackId}`);
    return response.data.data;
  },

  async reactivateTracking(gateway: string, trackId: string): Promise<any> {
    const response = await api.post(`/tracking/lookup/${gateway}/${trackId}/reactivate`);
    return response.data;
  },

  async getGatewayConfig(gateway: string, accountId: string, clientId: string): Promise<any> {
    const response = await api.get(`/tracking/gateway-config/${gateway}/${accountId}/${clientId}`);
    return response.data.data;
  },

  async validateCredentials(gateway: string, accountId: string, clientId: string, credentials: any, settings: any): Promise<any> {
    const response = await api.post(`/tracking/validate-credentials/${gateway}`, {
      accountId,
      clientId,
      credentials,
      settings
    });
    return response.data.data;
  },
};